import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, HostListener, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MarkdownModule } from 'ngx-markdown';
import { MatExpansionModule } from '@angular/material/expansion';
import { v4 as uuidv4 } from 'uuid';

import Resources from '@constants/resourses';
import { selectedAddressModel } from 'src/app/core/models/selectedAddress.interface';

import { PostcodeLookupComponent } from '../postcode-lookup/postcode-lookup.component';
import { LoginComponent } from '@shared/dialog/login/login.component';

import { UtilityService } from '@services/utility.service';
import { SessionService } from '@services/session.service';
import { TealiumUtagService } from '@services/utag.service';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { RestapiService } from '@services/restapi.service';
import { AppService } from '@services/app.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MarkdownModule, MatFormFieldModule, MatInputModule, PostcodeLookupComponent, MatDialogModule, MatCheckboxModule, MatExpansionModule],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];
  consentFormGroup: FormGroup;
  isLoggedIn: boolean = false;
  navigateToURL = (page: string) => this.utilityService?.navigateTo(page);

  id: string = 'EnergyComparison_PostcodeEntry';
  tealiumData: any = {};
  abVariant: string;
  ctmFlowVariant: string;
  googleAnalytics = inject(GoogleAnalyticsService);

  supplierConfig: any;
  regex: any;

  marketingConsentList: any[] = [];
  selectedConsent: any[] = [];
  CtmMarketingConsent: string;

  postcode: string = '';
  email: string = '';
  selectedAddress: selectedAddressModel;
  currentUsage: any = {};
  correlationId: string = '';
  isCurrentUsageLoaded: boolean = false;
  personalDetails: any = {};
  savedDetails: any = {};

  isReset: boolean = false;
  isSubmitting: boolean = false;

  queryParams: any;

  isFooterSticky: boolean = true;
  isAtBottom: boolean = false;

  constructor(
    private utilityService: UtilityService,
    private activatedRoute: ActivatedRoute,
    private sessionService: SessionService,
    private dialog: MatDialog,
    private tealiumService: TealiumUtagService,
    private fb: FormBuilder,
    private router: Router,
    private restAPI: RestapiService,
    private appService: AppService
  ) {
    this.tealiumData = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumData);
    this.email = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Email);
    this.CtmMarketingConsent = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.MarketingConsentChannels);
    this.isLoggedIn = this.utilityService.isLoggedIn();

    if (this.isLoggedIn) {
      this.personalDetails = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.PersonalDetails);
      this.savedDetails = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.SavedDetail);
    }

    this.subscriptions.push(
      this.utilityService.getDefaultSupplierConfiguration().subscribe({
        next: (res) => {
          this.regex = res?.data?.Generic?.Postcode?.pattern;
          this.supplierConfig = res?.data;

          // If visible then show the CTM Marketing Consent and email field
          const marketingConsent = res?.data?.CTMMarketingConsent;
          if (marketingConsent?.isVisible) {

            // CTM Marketing Consent
            this.marketingConsentList = (this.supplierConfig?.CTMMarketingConsent?.data?.communicationChannels).map((item: any, i: number) => {
              return {
                id: i + 1,
                name: item,
                isChecked: false
              };
            });

            // If already given consent then no need to show the consent
            if (!this.isLoggedIn) {
              // Show the consent form email and marketing consent
              this.consentFormGroup = this.fb.group({
                email: ['', [Validators.required, Validators.pattern(res?.data?.Generic?.Email?.pattern)]],
                consent: ['', [Validators.required]]
              });
            }
          }
        },
        error: (err) => {
          console.error('Error fetching configuration:', err);
        }
      })
    );

    // Resetting the session storage
    this.sessionService.removeItemFromLocal(Resources.SessionStorageKeys.QuotesData);
    this.postcode = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Postcode);
  }

  // Host listener to detect scroll events
  // This will be used to determine if the footer should be sticky or not
  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Get the distance from the bottom of the page
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const distanceToBottom = documentHeight - (scrollTop + windowHeight);

    // Check if we're near the bottom of the page (within 100px)
    this.isAtBottom = distanceToBottom < 100;

    // Footer should be sticky except when at the bottom of the page
    this.isFooterSticky = !this.isAtBottom;

    // No need to modify body classes for margin now that we're using position: sticky
  }

  ngOnInit(): void {
    // Initialize abVariant
    this.abVariant = this.utilityService.getCookie(Resources.ABtestingKeys.LANDING_PAGE_VARIANT_COOKIE) || 'A';
    this.ctmFlowVariant = this.utilityService.getCookie(Resources.ABtestingKeys.CTM_FLOW_VARIANT_COOKIE) || 'A';

    // Check if campaign ID is present in the url
    if (this.activatedRoute.snapshot.queryParams?.campaignId) {
      this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CampaignId, this.activatedRoute.snapshot.queryParams.campaignId);
    }

    // Query params
    if (this.activatedRoute.snapshot.queryParams) {
      this.queryParams = { ...this.activatedRoute.snapshot.queryParams };
    }

    // Google Analytics event
    this.googleAnalytics.track({
      page: 'Postcode',
      abVariant: this.abVariant,
      ctmFlowVariant: this.ctmFlowVariant
    }, 'page_view_postcode');

    // Initial check for footer position
    setTimeout(() => {
      this.onWindowScroll();
    }, 100);
  }

  // SignIn
  signIn(): void {
    const dialogRef = this.dialog.open(LoginComponent, {
      data: { placement: 'header_sign_in', target: 'login' },
      width: '570px',
      maxWidth: window.innerWidth <= 767 ? '95vw' : '569px',
      disableClose: true,
      autoFocus: false,
      panelClass: 'ctm-modal'
    });

    // After the dialog is closed, check the result
    dialogRef.afterClosed().subscribe(result => {
      // From postcode page
      this.tealiumData['page_name'] = 'PostcodeEntry';
      this.tealiumData['page_type'] = 'RiskJourney';
      this.tealiumData['customer_partialpostcode'] = this.utilityService.partialPostcode(this.postcode);
      this.tealiumData['base_url'] = window.location.href;
      if (!result) {
        // Closed loggedIn popup
        const email = this.sessionService.getItemFromLocal(Resources.SessionStorageKeys.Email);
        this.tealiumData['customer_id'] = this.utilityService.encryptIntoSha256(email);
        this.tealiumData['energycomparison_subscribed'] = !!email;

        // Trigger view event
        this.tealiumService.viewEvent(this.tealiumData);
      }
    });
  }

  // Get address details
  getAddressDetails(address: any): void {
    this.selectedAddress = address?.selectedAddress;
    // Get current usage
    this.getCurrentUsage();
  }

  // Reset Address
  resetAddress(): void {
    this.selectedAddress = undefined;
    this.isReset = true;
  }

  // Back
  back(): void {
    this.tealiumData['event_category'] = 'YourEnergy';
    this.tealiumData['event_label'] = 'BackButton';
    this.tealiumData['event_action'] = 'onClick';

    this.tealiumService.linkEvent(this.tealiumData);

    if (this.queryParams?._gl) {
      window.open(`https://www.comparethemarket.com/energy/?_gl=${this.queryParams?._gl}`, '_self');
    } else {
      window.open(`https://www.comparethemarket.com/energy`, '_self');
    }
  }

  tealiumDataSet(state: string): void {
    this.tealiumData = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumData) || {};
    this.tealiumData['page_name'] = 'PostcodeEntry';
    this.tealiumData['page_type'] = 'RiskJourney';
    this.tealiumData['event_category'] = 'YourEmail';
    if (state === 'focus') {
      this.tealiumData['event_label'] = 'EmailFieldEntry';
      this.tealiumData['event_action'] = 'onEntry';
    } else if (state === 'blur') {
      this.tealiumData['event_label'] = (this.consentFormGroup?.get('email')?.valid) ? 'EmailFieldExit' : 'InvalidEmail';
      this.tealiumData['event_action'] = 'onExit';
    }

    this.tealiumService.linkEvent(this.tealiumData);
  }

  // Submit
  submit(): void {
    // If consent is not visible and address is selected or consent form is valid and address is selected
    if (!!this.isLoggedIn && this.selectedAddress || this.consentFormGroup?.valid && this.selectedAddress) {
      this.isSubmitting = true;

      if (!this.correlationId) {
        this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsNewFlow, true);
        this.navigateToDashboard();
        return;
      } else if (this.currentUsage) {
        // User creation
        this.userCreation();
      } else {
        // Get current usage
        this.getCurrentUsage(true);
      }
    } else {
      // Show error message
      this.isSubmitting = false;
    }
  }

  // Navigate to dashboard
  navigateToDashboard(): void {
    this.tealiumData = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumData);
    // Set the email in local storage
    const userEmail = this.consentFormGroup?.get('email')?.value?.trim()?.toLowerCase();
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Email, (userEmail || this.email));
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.MarketingConsentChannels, this.consentFormGroup?.get('consent')?.value || this.CtmMarketingConsent);
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.IsConsentApproval, true);

    // Store into session storage
    this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.SelectedAddress, this.selectedAddress);
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, this.selectedAddress?.postcode || this.selectedAddress?.addressPostcode);

    // Tealium tags
    this.tealiumData['event_category'] = '';
    this.tealiumData['event_action'] = 'onClick';
    this.tealiumData['event_label'] = 'Submit';
    this.tealiumData['customer_partialpostcode'] = this.utilityService.partialPostcode(this.postcode);
    this.tealiumData['base_url'] = window.location.href;
    this.tealiumData['energycomparison_subscribed'] = !!this.consentFormGroup?.get('email')?.value || !!this.email;

    // Trigger tealium event
    this.tealiumService.linkEvent(this.tealiumData);

    if (userEmail) {
      this.googleAnalytics.track({ email: this.utilityService.getObfuscatedEmail(userEmail), abVariant: this.abVariant, ctmFlowVariant: this.ctmFlowVariant }, 'email_consent_provided');
    }

    // Redirect to dashboard
    this.queryParams = { ...this.queryParams, correlationId: this.correlationId || '' }
    this.router.navigate(['/information'], {
      queryParams: new URLSearchParams(this.queryParams),
    });
  }

  // Navigate to quote
  navigateToQuote(): void {
    this.tealiumData = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumData);
    // Set the email in local storage
    const userEmail = this.consentFormGroup?.get('email')?.value?.trim()?.toLowerCase();
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Email, (userEmail || this.email));
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.MarketingConsentChannels, this.consentFormGroup?.get('consent')?.value || this.CtmMarketingConsent);
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.IsConsentApproval, true);

    // Store into session storage
    this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.SelectedAddress, this.selectedAddress);
    this.sessionService.setItemIntoLocal(Resources.SessionStorageKeys.Postcode, this.selectedAddress?.postcode || this.selectedAddress?.addressPostcode);

    // Tealium tags
    this.tealiumData['event_category'] = '';
    this.tealiumData['event_action'] = 'onClick';
    this.tealiumData['event_label'] = 'Submit';
    this.tealiumData['customer_partialpostcode'] = this.utilityService.partialPostcode(this.postcode);
    this.tealiumData['base_url'] = window.location.href;
    this.tealiumData['energycomparison_subscribed'] = !!this.consentFormGroup?.get('email')?.value || !!this.email;

    // Trigger tealium event
    this.tealiumService.linkEvent(this.tealiumData);

    if (userEmail) {
      this.googleAnalytics.track({ email: this.utilityService.getObfuscatedEmail(userEmail), abVariant: this.abVariant, ctmFlowVariant: this.ctmFlowVariant }, 'email_consent_provided');
    }

    this.appService.updateApplicationStatus('update');

    // Redirect to energy result page with correlationId
    this.router.navigate(['/energy-result'], {
      queryParams: { correlationId: this.correlationId }
    });
  }

  // Get current usage
  getCurrentUsage(isUserCreation: boolean = false): void {
    this.isCurrentUsageLoaded = false;
    // Edge case: If addressId not available then no need to call an current ussage API.
    if (!this.selectedAddress?.addressId) {
      // Navigate to postcode lookup page
      this.router.navigate(['/']);
      return;
    }

    const payload = {
      address_id: this.selectedAddress?.addressId,
      postcode: this.selectedAddress?.addressPostcode,
      aquistion_party: 'UI',
      gbgVersion: this.selectedAddress?.gbgVersion
    };

    // For loggedIn user
    if (this.isLoggedIn) {
      payload['email'] = this.email;
      payload['addressLine'] = this.selectedAddress?.addressText;

      // MPAN
      if (this.selectedAddress?.mpan || this.savedDetails?.electricDetails?.mpan || this.savedDetails?.mpan) {
        payload['mpan'] = this.selectedAddress?.mpan || this.savedDetails?.electricDetails?.mpan || this.savedDetails?.mpan;
      }

      // MPRN
      if (this.selectedAddress?.mprn || this.savedDetails?.gasDetails?.mprn || this.savedDetails?.mprn) {
        payload['mprn'] = this.selectedAddress?.mprn || this.savedDetails?.gasDetails?.mprn || this.savedDetails?.mprn;
      }
    }
    this.subscriptions.push(
      this.restAPI.postAPI('/v1/users/currentusageWrapper', payload).subscribe({
        next: (data) => {
          // Normalise keys
          data['correlationId'] = data?.correlation_id || data?.correlationId;
          delete data?.correlation_id;

          data['addressId'] = payload?.address_id || data?.addressId;
          delete data?.address_id;

          // If triggerFrom is not available then not to execute
          this.currentUsage = data;
          this.correlationId = this.currentUsage?.correlationId;

          // Update MPAN and MPRN
          // MPAN
          if (this.currentUsage?.mpan) {
            this.selectedAddress.mpan = this.currentUsage?.mpan;
          }
          // MPRN
          if (this.currentUsage?.mprn) {
            this.selectedAddress.mprn = this.currentUsage?.mprn;
          }

          // Store into session storage
          this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CorrelationId, this.correlationId);
          this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.DnoId, this.selectedAddress?.dnoId || this.currentUsage?.dnoRegion);
          this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.SelectedAddress, this.selectedAddress);
          this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CurrentUsage, this.currentUsage);

          // Enable 'Next' button after current usage is loaded
          this.isCurrentUsageLoaded = true;

          // If user creation is not triggered then trigger user creation
          if (isUserCreation) {
            this.userCreation();
          }
        }, error: (err) => {
          this.isCurrentUsageLoaded = true;
          this.correlationId = err?.error?.correlationId || err?.error?.msg?.correlationId;

          if (!!this.correlationId) {
            this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.CorrelationId, this.correlationId);
          } else {
            this.sessionService.removeItemFromSession(Resources.SessionStorageKeys.CorrelationId);
          }
        }
      })
    );
  }

  // User creation
  userCreation(): void {
    const markertingConsent = this.consentFormGroup?.get('consent')?.value || this.CtmMarketingConsent;

    const triggerTime = new Date().getTime();
    const payload = {
      user: {
        email: (this.consentFormGroup?.get('email')?.value || this.email)?.trim()?.toLowerCase(),
        uuidToken: uuidv4(),
        switchRecommendation: (markertingConsent !== 'Don\'t contact me'),
        contactConsent: markertingConsent !== 'Don\'t contact me' ? markertingConsent?.split(',') : [],
        marketingConsent: (markertingConsent !== 'Don\'t contact me'),
      }
    };

    // Personal details
    if (this.personalDetails) {
      if (this.personalDetails?.firstName) {
        payload.user['firstName'] = this.personalDetails?.firstName;
      }
      if (this.personalDetails?.lastName) {
        payload.user['lastName'] = this.personalDetails?.lastName;
      }
      if (this.personalDetails?.phone) {
        payload.user['mobile'] = this.personalDetails?.phone;
      }
    }

    this.subscriptions.push(
      this.restAPI.postAPI(`/v1/users?correlationId=${this.correlationId}`, payload).subscribe({
        next: (res) => {
          this.email = this.consentFormGroup?.get('email')?.value || this.email;
          // User onboarding
          this.userOnBoarding();
        }, error: (err) => {
          console.log('Error fetching user creation:', err);
          // If email is not valid then show error message
          if (err?.error?.additionalAttributes?.attribute === 'email') {
            // If email is not valid then show error message
            this.consentFormGroup.get('email')?.setErrors({ 'invalid': err?.error?.additionalAttributes?.displayText });

            setTimeout(() => {
              // Scroll to the email field
              const errorElementRef = document.querySelector('.ng-invalid');
              if (errorElementRef) {
                errorElementRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }
          const responseTime = (new Date().getTime() - triggerTime) / 1000;
          this.isSubmitting = false;

          // Trigger error email
          this.utilityService.sendAPIError('User creation API Failed', { userCreation: payload }, { userCreation: err }, responseTime);
        }
      })
    );
  }

  // User onboarding
  userOnBoarding(): void {
    const triggerTime = new Date().getTime();
    const payload = {
      email: (this.consentFormGroup?.get('email')?.value || this.email)?.trim()?.toLowerCase(),
      correlationId: this.correlationId,
      uuidToken: uuidv4()
    };

    this.subscriptions.push(
      this.restAPI.postAPI('/v1/users/onboard', payload).subscribe({
        next: (data) => {
          if (this.ctmFlowVariant === 'A') {
            const fuelType = this.utilityService.getFuelType(this.currentUsage, 'current-usage');
            if ((fuelType === Resources.EnergyType.BOTH && (!this.currentUsage?.electricity?.gocoSupplier || !this.currentUsage?.electricity?.tarrif || !this.currentUsage?.gas?.gocoSupplier || !this.currentUsage?.gas?.tarrif))
              || (fuelType === Resources.EnergyType.ELECTRICITY && (!this.currentUsage?.electricity?.gocoSupplier || !this.currentUsage?.electricity?.tarrif))
              || (fuelType === Resources.EnergyType.GAS && (!this.currentUsage?.gas?.gocoSupplier || !this.currentUsage?.gas?.tarrif))
              || !this.currentUsage?.dnoRegion
            ) {
              this.sessionService.setItemIntoSession(Resources.SessionStorageKeys.IsNewFlow, true);
              // Navigate to dashboard
              this.navigateToDashboard();
            } else {
              // Navigate to quote
              this.navigateToQuote();
            }
          } else {
            // Navigate to dashboard
            this.navigateToDashboard();
          }

        }, error: (err) => {
          console.log('Error fetching user onboarding:', err);
          const responseTime = (new Date().getTime() - triggerTime) / 1000;
          this.isSubmitting = false;

          // Trigger error email
          this.utilityService.sendAPIError('User onboarding API Failed', { userOnboarding: payload }, { userOnboarding: err }, responseTime);
        }
      })
    );
  }

  // Selected Marketing consent
  selectedMarketingConsent(event: any, options: any): void {
    this.tealiumData = this.sessionService.getItemFromSession(Resources.SessionStorageKeys.TealiumData);
    if (event.source.value === "Don't contact me") {
      if (event.checked) {
        this.selectedConsent.push(event.source.value);
        this.selectedConsent = this.selectedConsent.filter((consent) => consent === "Don't contact me");
      } else {
        this.selectedConsent = this.selectedConsent.filter((consent) => consent !== "Don't contact me");
      }
      this.marketingConsentList.map((option: any) => {
        option['isChecked'] = (option.name === "Don't contact me");
      })
    } else {
      this.marketingConsentList.map((option: any) => {
        if (option.name === event.source.value) {
          option['isChecked'] = event.checked;
        }

        if (option.name === "Don't contact me") {
          option['isChecked'] = false;
        }
      });
      if (event.checked) {
        this.selectedConsent.push(event.source.value);
        this.selectedConsent = this.selectedConsent.filter((consent) => consent !== "Don't contact me");
      } else {
        this.selectedConsent = this.selectedConsent.filter((consent) => consent !== event.source.value);
      }
    }

    // Update form control
    this.consentFormGroup.patchValue({
      consent: (this.selectedConsent).join(', ')
    });

    // Tealium CTM marketing contact preferences
    const contactPreferences = [];
    // Iterate and create contact preferences
    this.selectedConsent.map((preference: string) => {
      const preferenceData = {};
      preferenceData[preference] = true;
      contactPreferences.push(preferenceData);
    });

    // Tealium tags
    this.tealiumData['event_category'] = `ContactPreferences_CTM_${(event.source.value === "Don't contact me") ? 'DontContactMe' : event.source.value}`;
    this.tealiumData['event_label'] = `${event.checked ? 'Yes' : 'No'}`;
    this.tealiumData['contactPreferences'] = contactPreferences;
    this.tealiumData['event_action'] = `${event.checked ? 'checked' : 'unchecked'}`;

    // Trigger tealium
    this.tealiumService.linkEvent(this.tealiumData);

    this.consentFormGroup.get('consent')?.markAllAsTouched();
  }

  ngOnDestroy(): void {
    // Clean up body class when component is destroyed
    document.body.classList.remove('fixed-bottom-margin');

    // Unsubscribe from all subscriptions
    this.subscriptions.forEach((subscription) => {
      subscription.unsubscribe();
    });
  }
}